/**
 * InputHandler - Handles keyboard and mouse input for the game
 */

import { useGameStore } from '@/state/gameStore';

export interface InputHandlerCallbacks {
  onToggleFactionPanel: () => void;
  onToggleDiplomacyPanel: () => void;
  onToggleTechPanel: () => void;
  onToggleHelpPanel: () => void;
  onToggleFogOfWar: () => void;
  onToggleSettingsPanel: () => void;
  onToggleSaveLoadMenu: () => void;
  onDemolishBuildings: () => void;
  onDeselectBuilding: () => void;
  onCancelBuildingPlacement: () => void;
  onQuickSave: () => void;
  onQuickLoad: () => void;
  onSetControlGroup: (groupNum: number) => void;
  onSelectControlGroup: (groupNum: number) => void;
  isSettingsPanelVisible: () => boolean;
  isSaveLoadMenuVisible: () => boolean;
  hasSelectedBuildings: () => boolean;
  hasSelectedBuildingType: () => boolean;
}

export class InputHandler {
  private callbacks: InputHandlerCallbacks;
  private lastSpeed: number = 1;
  private keydownHandler: (e: KeyboardEvent) => void;

  constructor(callbacks: InputHandlerCallbacks) {
    this.callbacks = callbacks;
    this.keydownHandler = this.handleKeydown.bind(this);
    window.addEventListener('keydown', this.keydownHandler);
  }

  private handleKeydown(e: KeyboardEvent): void {
    // Ctrl+Number for control groups
    if (e.ctrlKey && e.key >= '1' && e.key <= '9') {
      const groupNum = parseInt(e.key);
      this.callbacks.onSetControlGroup(groupNum);
      return;
    }

    // Number keys to select control groups
    if (!e.ctrlKey && e.key >= '1' && e.key <= '9') {
      const groupNum = parseInt(e.key);
      this.callbacks.onSelectControlGroup(groupNum);
      return;
    }

    // Space to pause/resume
    if (e.code === 'Space') {
      e.preventDefault();
      const currentSpeed = useGameStore.getState().gameSpeed;
      if (currentSpeed === 0) {
        useGameStore.getState().setGameSpeed(this.lastSpeed || 1);
      } else {
        this.lastSpeed = currentSpeed;
        useGameStore.getState().setGameSpeed(0);
      }
    }

    // +/= to increase speed
    if (e.key === '+' || e.key === '=') {
      const currentSpeed = useGameStore.getState().gameSpeed;
      if (currentSpeed < 3) {
        useGameStore.getState().setGameSpeed(currentSpeed + 1);
      }
    }

    // - to decrease speed
    if (e.key === '-') {
      const currentSpeed = useGameStore.getState().gameSpeed;
      if (currentSpeed > 0) {
        useGameStore.getState().setGameSpeed(currentSpeed - 1);
      }
    }

    // F to toggle faction panel
    if (e.key === 'f' || e.key === 'F') {
      this.callbacks.onToggleFactionPanel();
    }

    // R to toggle diplomacy panel (Relations)
    if (e.key === 'r' || e.key === 'R') {
      this.callbacks.onToggleDiplomacyPanel();
    }

    // T to toggle tech panel
    if (e.key === 't' || e.key === 'T') {
      this.callbacks.onToggleTechPanel();
    }

    // H to toggle help panel
    if (e.key === 'h' || e.key === 'H') {
      this.callbacks.onToggleHelpPanel();
    }

    // V to toggle fog of war
    if (e.key === 'v' || e.key === 'V') {
      this.callbacks.onToggleFogOfWar();
    }

    // Delete or Backspace to demolish selected buildings
    if (e.key === 'Delete' || e.key === 'Backspace') {
      if (this.callbacks.hasSelectedBuildings()) {
        e.preventDefault();
        this.callbacks.onDemolishBuildings();
      }
    }

    // F5 to quick save
    if (e.key === 'F5') {
      e.preventDefault();
      this.callbacks.onQuickSave();
    }

    // F9 to quick load
    if (e.key === 'F9') {
      e.preventDefault();
      this.callbacks.onQuickLoad();
    }

    // Escape key - handle in priority order
    if (e.key === 'Escape') {
      // 1. Close settings panel if open
      if (this.callbacks.isSettingsPanelVisible()) {
        this.callbacks.onToggleSettingsPanel();
        return;
      }
      // 2. Close save/load menu if open
      if (this.callbacks.isSaveLoadMenuVisible()) {
        this.callbacks.onToggleSaveLoadMenu();
        return;
      }
      // 3. Deselect building if selected
      if (this.callbacks.hasSelectedBuildings()) {
        this.callbacks.onDeselectBuilding();
        return;
      }
      // 4. Cancel building placement if active
      if (this.callbacks.hasSelectedBuildingType()) {
        this.callbacks.onCancelBuildingPlacement();
        return;
      }
      // 5. Open save/load menu
      this.callbacks.onToggleSaveLoadMenu();
    }

    // O for Options/Settings
    if (e.key === 'o' || e.key === 'O') {
      this.callbacks.onToggleSettingsPanel();
    }
  }

  dispose(): void {
    window.removeEventListener('keydown', this.keydownHandler);
  }
}
